import React from "react";
import { Box, Typography, IconButton, keyframes } from "@mui/material";
import {
  Close as CloseIcon,
  LocalFireDepartment as FlameIcon,
} from "@mui/icons-material";
import { getComboLabel } from "../../utils/triviaArcade";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.65; }
`;

const TriviaGameHUD = ({
  currentIndex,
  total,
  score,
  timeLeft,
  timeLimit,
  streak,
  onQuit,
  reducedMotion,
  compact = false,
}) => {
  const timerSize = compact ? 56 : 96;
  const radius = compact ? 24 : 42;
  const circumference = 2 * Math.PI * radius;
  const progress = timeLimit > 0 ? timeLeft / timeLimit : 0;
  const dashOffset = circumference * (1 - progress);
  const urgent = timeLeft <= 5;
  const combo = getComboLabel(streak);

  return (
    <Box sx={{ mb: compact ? 1.5 : 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: compact ? 1 : 2,
        }}
      >
        <IconButton aria-label="Quit game" onClick={onQuit} size="small">
          <CloseIcon />
        </IconButton>
        <Typography variant="body1" fontWeight={600}>
          Question {currentIndex + 1}/{total}
        </Typography>
        <Typography variant="body1" fontWeight={600}>
          Score: {score}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mb: compact ? 1 : 2 }}>
        <Box sx={{ position: "relative", width: timerSize, height: timerSize }}>
          <svg width={timerSize} height={timerSize} viewBox={`0 0 ${timerSize} ${timerSize}`}>
            <circle
              cx={timerSize / 2}
              cy={timerSize / 2}
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.12)"
              strokeWidth={compact ? 5 : 6}
            />
            <circle
              cx={timerSize / 2}
              cy={timerSize / 2}
              r={radius}
              fill="none"
              stroke={urgent ? "#ef4444" : "#ec4899"}
              strokeWidth={compact ? 5 : 6}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${timerSize / 2} ${timerSize / 2})`}
              style={
                urgent && !reducedMotion
                  ? { animation: `${pulse} 1s ease-in-out infinite` }
                  : undefined
              }
            />
          </svg>
          <Typography
            variant={compact ? "body1" : "h6"}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              color: urgent ? "error.main" : "text.primary",
            }}
          >
            {timeLeft}s
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 1 }}>
        {Array.from({ length: total }).map((_, index) => (
          <Box
            key={index}
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor:
                index < currentIndex
                  ? "success.main"
                  : index === currentIndex
                    ? "primary.main"
                    : "rgba(255,255,255,0.2)",
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
        <FlameIcon sx={{ color: "#f97316", fontSize: 20 }} />
        <Typography variant="body2">Streak: {streak}</Typography>
        {combo && (
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 999,
              bgcolor: "rgba(236, 72, 153, 0.2)",
              color: "#ec4899",
              fontWeight: 700,
            }}
          >
            {combo}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TriviaGameHUD;
